import { parseProblem } from '@reos/api-client';
import type { AcceptanceMissingItem } from '@reos/duediligence';
import { baseUrl } from './api.js';
import { DomainApiError } from './api-error.js';
import { getAuditRunStatus } from './audit-api.js';

/**
 * Krok A5: klient nad `/v1/admin/reports/*` (@reos/commerce) - fronta
 * „Ke schvaleni" a rozhodnuti Revizora. Rucni typy ze stejneho duvodu jako
 * v `orders-api.ts` (krok C3): `docs/api/openapi.json` je zastaraly a plna
 * regenerace by do tohohle kroku vnesla desetitisice nesouvisejicich radku.
 * Tvar odpovida `pendingReleaseSchema` / `orderSchema` v api-schemas.ts.
 */

export interface PendingReleaseItem {
  readonly reviewId: string;
  readonly orderId: string;
  readonly orderNumber: string;
  readonly orderStatus: string;
  readonly productCode: string;
  readonly productNameKey: string;
  readonly addressLabel: string;
  readonly propertyKind: string;
  readonly triggerCodes: readonly string[];
  readonly aiCodes: readonly string[];
  readonly openHumanTasks: number;
  readonly costCzk: string | null;
  readonly reviewSummary: string | null;
  readonly needsReviewReason: string | null;
  readonly caseId: string | null;
  readonly auditRunId: string | null;
  readonly exportId: string | null;
  readonly reportReady: boolean;
  /** Krok REV1: uz schvaleno, dokument se teprve vyrabi. */
  readonly releaseApproved: boolean;
  readonly createdAt: string;
  readonly paidAt: string | null;
}

async function unwrapFetch<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const problem = await parseProblem(res).catch(() => null);
    throw new DomainApiError(problem?.detail ?? problem?.title ?? `HTTP ${res.status}`, res.status, problem?.type);
  }
  return (await res.json()) as T;
}

export async function listPendingReleases(): Promise<readonly PendingReleaseItem[]> {
  const res = await fetch(`${baseUrl}/v1/admin/reports/pending-release`, {
    credentials: 'include',
    headers: { accept: 'application/json' },
  });
  const body = await unwrapFetch<{ items: PendingReleaseItem[] }>(res);
  return body.items;
}

/**
 * Ovolneni (schvaleni + odeslani e-mailem) reportu zakaznikovi.
 *
 * Krok UX2: vraci `orderSchema`, ne `void` — po uvolneni z fronty zmizi a
 * stav „odesláno kdy komu“ zustane jen v odpovedi. Precte se z ni
 * `deliveredAt` a `email`.
 */
export interface ReleasedOrder {
  readonly id: string;
  readonly orderNumber: string;
  readonly status: string;
  readonly email: string;
  readonly deliveredAt: string | null;
}

export async function releaseReport(orderId: string, note: string): Promise<ReleasedOrder> {
  const res = await fetch(`${baseUrl}/v1/admin/reports/${encodeURIComponent(orderId)}/release`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(note.trim().length === 0 ? {} : { note: note.trim() }),
  });
  return unwrapFetch<ReleasedOrder>(res);
}

/**
 * Krok REV1: SCHVALENI VYDANI u objednavky, pro kterou klientsky report
 * jeste nevznikl (brana uplnosti nebo brana vydani ho zadrzela). Report se
 * vykresli a dorucí sam, jakmile PDF dobehne - proto `status` v odpovedi
 * jeste nemusi byt `DELIVERED`.
 */
export async function approveReport(orderId: string, note: string): Promise<ReleasedOrder> {
  const res = await fetch(`${baseUrl}/v1/admin/reports/${encodeURIComponent(orderId)}/approve`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(note.trim().length === 0 ? {} : { note: note.trim() }),
  });
  return unwrapFetch<ReleasedOrder>(res);
}

/** Vraceni k prepracovani - duvod je POVINNY (vynucuje i API). */
export async function returnReport(orderId: string, reason: string): Promise<void> {
  const res = await fetch(`${baseUrl}/v1/admin/reports/${encodeURIComponent(orderId)}/return`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ reason }),
  });
  await unwrapFetch<unknown>(res);
}

/**
 * Krok REV1 bod 1: CO BRANE KVALITY U TOHOHLE BEHU CHYBELO, lidsky.
 *
 * Revizor to do REV1 videl jen jako kody spoustecu a `needsReviewReason`, takze
 * nevedel, CO ma doplnit. Polozky `acceptance.missing` uz na serveru jsou -
 * `audit_runs.acceptance` je vystavene pres `GET /v1/audit-runs/:id`, ktere cte
 * `getAuditRunStatus`. Zadny novy endpoint ani sloupec tedy nevznika a cteni
 * toho behu zustava na jednom miste (`audit-api.ts`).
 *
 * Tvar `acceptance` je v DTO `Record<string, unknown>` (jsonb), takze se tu
 * ctou jen polozky, ktere maji `code`; cokoli jineho se ticha preskoci.
 * Novejsi verze engine s neznamym kodem tim obrazovku neshodi (poucení N6) -
 * text si pak slozi `formatAcceptanceMissing` z i18n katalogu.
 */
function toMissingItem(entry: unknown): AcceptanceMissingItem | null {
  if (typeof entry !== 'object' || entry === null) return null;
  const row = entry as Record<string, unknown>;
  const code = row['code'];
  if (typeof code !== 'string' || code.length === 0) return null;
  const text = (key: string): string | null => (typeof row[key] === 'string' ? (row[key] as string) : null);
  return {
    code: code as AcceptanceMissingItem['code'],
    sectionId: text('sectionId'),
    questionKey: text('questionKey'),
    sourceId: text('sourceId'),
    ref: text('ref'),
  };
}

export async function fetchAcceptanceMissing(
  auditRunId: string,
): Promise<readonly AcceptanceMissingItem[]> {
  const run = await getAuditRunStatus(auditRunId);
  const raw = (run.acceptance ?? {})['missing'];
  if (!Array.isArray(raw)) return [];
  return raw.map(toMissingItem).filter((item): item is AcceptanceMissingItem => item !== null);
}

/** Odkaz na nahled hotoveho PDF (existujici admin cesta z kroku K1b). */
export function reportPreviewUrl(exportId: string): string {
  return `${baseUrl}/v1/dd-exports/${encodeURIComponent(exportId)}/download`;
}
